import { FieldArray, FieldArrayRenderProps } from 'formik';
import React, { useContext, useState } from 'react';
import styled from 'styled-components/macro';
import { assertNotNull } from '../../../../helpers/nullHelpers';
import { ReactComponent as DeleteIcon } from '../../../../images/icons/delete.svg';
import { ButtonIcon, SecondaryButton } from '../../../../shared/Button';
import { EmptyPlaceholder } from '../../../../shared/EmptyPlaceholder';
import { Expandable } from '../../../../shared/Expandable';
import { FormContext } from '../../../../shared/form/Form';
import { SearchSelect } from '../../../../shared/form/inputs/SearchSelect';
import { TextInputField } from '../../../../shared/form/inputs/TextInputField';
import { Header4 } from '../../../../shared/Headers';
import { Table, TBody, Td, THead, Tr } from '../../../../shared/Table';
import { colourNeutral07 } from '../../../../styling/colours';
import { spacing02, spacing04, spacing06 } from '../../../../styling/spacing';
import {
  ActionButtonContainer,
  ActionButtonTd,
  ActionButtonTh,
  DescriptionTd,
  DescriptionTh,
  PartCodeTd,
  PartCodeTh,
  QuantityTd,
} from '../../../bill-of-materials/BillOfMaterialsItemsTable';
import { QuantityColumnHeader } from '../../../bill-of-materials/QuantityColumnHeader';
import { AddItemButton } from '../AddItemButton';
import { DeleteButton } from '../DeleteAndDuplicateButtons';
import { DeleteConfirmation } from '../DeleteConfirmation';
import { QuoteOrDesignFormContext } from '../QuoteOrDesignFormContext';
import { QuoteOrDesignFormExpandControlChevron } from '../QuoteOrDesignFormExpandControlChevron';
import { SectionHeader } from '../SectionHeader';
import { unitFieldName } from '../quoteOrDesignFormFieldNameHelpers';
import { UnitFormModel } from './unitFormModel';
import { getInitialUnitExtraPartFormModel } from './unitExtraPartFormModel';

export type ExtraPartsTechnology = 'underfloorHeating' | 'heatPump';

const labelsByTechnology: { [technology in ExtraPartsTechnology]: string } = {
  underfloorHeating: 'Extra Underfloor Heating Parts',
  heatPump: 'Extra Heat Pump Parts',
};

const getExtraPartsFor = (unit: UnitFormModel, technology: ExtraPartsTechnology) =>
  technology === 'underfloorHeating' ? unit.underfloorHeatingExtraParts : unit.heatPumpExtraParts;

type SectionState = 'notAdded' | 'collapsed' | 'expanded';

type Props = {
  unitIndex: number;
  technology: ExtraPartsTechnology;
};

export const UnitExtraPartsField = ({ unitIndex, technology }: Props) => {
  const [selectedPartId, setSelectedPartId] = useState<number | null>(null);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const { isDisabled } = useContext(FormContext);
  const { referenceData, formProps } = assertNotNull(useContext(QuoteOrDesignFormContext));
  const { setFieldValue } = formProps;

  const label = labelsByTechnology[technology];
  const extraPartsFieldName = unitFieldName(unitIndex, `${technology}ExtraParts`);
  const extraParts = getExtraPartsFor(formProps.values.units[unitIndex], technology);

  const [sectionState, setSectionState] = useState<SectionState>(extraParts.length > 0 ? 'expanded' : 'notAdded');
  const isExpanded = sectionState === 'expanded';

  const addExtraPart = (arrayHelpers: FieldArrayRenderProps) => {
    if (selectedPartId == null) {
      return;
    }

    arrayHelpers.push(getInitialUnitExtraPartFormModel(selectedPartId));
    setSelectedPartId(null);
  };

  const deleteSection = () => {
    setFieldValue(extraPartsFieldName, []);

    setIsConfirmingDelete(false);
    setSectionState('notAdded');
  };

  if (sectionState === 'notAdded') {
    return isDisabled ? null : (
      <ShowExtraPartsButton onClick={() => setSectionState('expanded')} aria-label={`Add ${label}`}>
        + Extra Parts
      </ShowExtraPartsButton>
    );
  }

  return (
    <FieldArray name={extraPartsFieldName}>
      {arrayHelpers => (
        <Container>
          <SectionHeader>
            <Header4>{label}</Header4>
            <QuoteOrDesignFormExpandControlChevron
              onClick={() => setSectionState(isExpanded ? 'collapsed' : 'expanded')}
              isExpanded={isExpanded}
            />
            {!isDisabled && <DeleteButton onClick={() => setIsConfirmingDelete(true)} />}
          </SectionHeader>
          <DeleteConfirmation
            isConfirmingDelete={isConfirmingDelete}
            setIsConfirmingDelete={setIsConfirmingDelete}
            deleteItem={deleteSection}
          />
          <Expandable isExpanded={isExpanded}>
            <ExtraPartsTable>
              <THead>
                <Tr>
                  <PartCodeTh>Part Code</PartCodeTh>
                  <QuantityColumnHeader fieldArrayName={extraPartsFieldName} errors={formProps.errors} />
                  <DescriptionTh>Description</DescriptionTh>
                  <ActionButtonTh />
                </Tr>
              </THead>
              <TBody>
                {extraParts.map((extraPart, index) => {
                  const part = referenceData.extraPartsById[extraPart.partId];
                  const partName = part == null ? extraPart.partId.toString() : part.name;

                  return (
                    <Tr key={index}>
                      <PartCodeTd>{partName}</PartCodeTd>
                      <QuantityTd>
                        <TextInputField name={`${extraPartsFieldName}.${index}.quantity`} label={null} />
                      </QuantityTd>
                      <DescriptionTd>{part == null ? null : part.description}</DescriptionTd>
                      <ActionButtonTd>
                        {!isDisabled && (
                          <ActionButtonContainer>
                            <SecondaryButton
                              onClick={() => arrayHelpers.remove(index)}
                              disabled={false}
                              title={`Delete ${partName}`}
                            >
                              <ButtonIcon>
                                <DeleteIcon />
                              </ButtonIcon>
                            </SecondaryButton>
                          </ActionButtonContainer>
                        )}
                      </ActionButtonTd>
                    </Tr>
                  );
                })}
                {extraParts.length === 0 && (
                  <Tr>
                    <Td colSpan={4}>
                      <EmptyPlaceholder message={`No ${label} have been added`} />
                    </Td>
                  </Tr>
                )}
              </TBody>
            </ExtraPartsTable>
            {!isDisabled && (
              <AddPartContainer>
                <AddPartSearchSelectContainer>
                  <SearchSelect<number | null>
                    onChange={setSelectedPartId}
                    value={selectedPartId}
                    options={referenceData.extraPartOptions}
                    placeholder="Enter a Part Code"
                    minimumSearchCharacters={1}
                  />
                </AddPartSearchSelectContainer>
                <SecondaryButton onClick={() => addExtraPart(arrayHelpers)} disabled={selectedPartId == null}>
                  Add Part
                </SecondaryButton>
              </AddPartContainer>
            )}
          </Expandable>
        </Container>
      )}
    </FieldArray>
  );
};

const ShowExtraPartsButton = styled(AddItemButton)`
  margin-top: ${spacing04};
  margin-bottom: 0;
`;

const Container = styled.div`
  margin-top: ${spacing06};
`;

const ExtraPartsTable = styled(Table)`
  table-layout: fixed;
  background-color: ${colourNeutral07};
`;

const AddPartContainer = styled.div`
  margin-top: ${spacing06};
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-end;
`;

const AddPartSearchSelectContainer = styled.div`
  display: inline-block;
  position: relative;
  width: 500px;
  margin-right: ${spacing02};
`;
